import type { FixedParam, FixedClass, DependentParam, TextureSoil } from '../types/parameters.types';

export interface EditValues {
	low: string;
	medium: string;
	high: string;
}

export const parseEditValues = (values: EditValues) => {
	const lowMax = parseFloat(values.low.replace(/[<≤]/g, '').trim());
	const medParts = values.medium.split('-').map((s) => parseFloat(s.trim()));
	const medMin = isNaN(medParts[0]) ? 0 : medParts[0];
	const medMax = isNaN(medParts[1]) ? medMin : medParts[1];
	const highMin = parseFloat(values.high.replace('>', '').trim());
	return {
		lowMax: isNaN(lowMax) ? 0 : lowMax,
		medMin,
		medMax,
		highMin: isNaN(highMin) ? 0 : highMin,
	};
};

export const formatFixedClass = (param: FixedParam, classification: string) => {
	const cls = param.fixedClasses.find((c) => c.classification === classification);
	if (!cls) return '—';
	if (classification === 'Baixo') return `< ${cls.maxValue}`;
	if (classification === 'Medio' && cls.minValue !== undefined)
		return `${cls.minValue} - ${cls.maxValue}`;
	if (classification === 'Alto' && cls.minValue !== undefined) return `> ${cls.minValue}`;
	return `${cls.maxValue}`;
};

export const buildFixedUpdatedParam = (param: FixedParam, values: EditValues): FixedParam => {
	const { lowMax, medMin, medMax, highMin } = parseEditValues(values);
	const newFixedClasses: FixedClass[] = param.fixedClasses.map((cls) => {
		if (cls.classification === 'Baixo') return { ...cls, maxValue: lowMax };
		if (cls.classification === 'Medio') return { ...cls, minValue: medMin, maxValue: medMax };
		if (cls.classification === 'Alto') return { ...cls, minValue: highMin };
		return cls;
	});
	return { ...param, fixedClasses: newFixedClasses };
};

export const getClassesForTexture = (param: DependentParam, texture: TextureSoil) => {
	const rule = param.rules[0];
	if (!rule) return { low: '—', medium: '—', high: '—' };
	const cls = rule.dependentClasses.find((c) => c.texture === texture);
	if (!cls) return { low: '—', medium: '—', high: '—' };

	const lowClass = cls.fixedClasses.find((c) => c.classification === 'Baixo');
	const medClass = cls.fixedClasses.find((c) => c.classification === 'Medio');
	const highClass = cls.fixedClasses.find((c) => c.classification === 'Alto');

	const formatLow = (fc: typeof lowClass) => (!fc ? '—' : `< ${fc.maxValue}`);
	const formatMed = (fc: typeof medClass) => {
		if (!fc) return '—';
		if (fc.minValue !== undefined) return `${fc.minValue} - ${fc.maxValue}`;
		return `≤ ${fc.maxValue}`;
	};
	const formatHigh = (fc: typeof highClass) => {
		if (!fc) return '—';
		if (fc.minValue !== undefined) return `> ${fc.minValue}`;
		return `> ${fc.maxValue}`;
	};

	return { low: formatLow(lowClass), medium: formatMed(medClass), high: formatHigh(highClass) };
};

export const buildMacroUpdatedParam = (
	param: DependentParam,
	values: EditValues,
	texture: TextureSoil
): DependentParam => {
	const { lowMax, medMin, medMax, highMin } = parseEditValues(values);
	const newFixedClasses: FixedClass[] = [
		{ classification: 'Baixo', maxValue: lowMax },
		{ classification: 'Medio', minValue: medMin, maxValue: medMax },
		{ classification: 'Alto', minValue: highMin, maxValue: 9999 },
	];
	return {
		...param,
		rules: param.rules.map((rule, i) =>
			i === 0
				? {
						...rule,
						dependentClasses: rule.dependentClasses.map((dc) =>
							dc.texture === texture ? { ...dc, fixedClasses: newFixedClasses } : dc
						),
					}
				: rule
		),
	};
};
